import { z } from "zod";
import {
  subscriptionPlanSchema,
  type SubscriptionPlan,
} from "./subscription-plan";

// TES-1034 S8 (Emelie, points 5-6) — the prescription transfer patient's own
// subscription choice in F-07. A PATIENT choice, not a medical subscription
// decision: the doctor neither chooses nor approves it, and it is kept apart
// from his medical decision throughout.
//
// She may choose between the two options only when her answers show at
// least twelve months of earlier treatment elsewhere; otherwise eligibility
// is unclear and only the option with a personal coach is offered. That is
// not a medical risk flag.

// ---- F-07's answer keys (content v10) ---------------------------------------

/** "Have you used the treatment for at least twelve months in total?" */
export const PT_TWELVE_MONTHS_KEY = "pt_twelve_months_total";
/**
 * "Have you had a longer pause during that time?" F-07 v10 and v11 only
 * (Ö-285 a). Kept so their submissions stay readable; v12 drops it again.
 */
export const PT_LONG_BREAK_KEY = "pt_long_break";
export const PT_LONG_BREAK_DETAILS_KEY = "pt_long_break_details";
/**
 * "Have you paused the treatment?" (yes / no / unsure), the single pause
 * question. F-07 v1-v9 asked it as yes / no; v12 (TES-1099, Ö-292) brings it
 * back in place of the longer-pause question. Medically relevant, never a
 * bar to the option without a coach.
 */
export const PT_BREAK_KEY = "pt_break";
export const PT_BREAK_DETAILS_KEY = "pt_break_details";
/** The choice between the two options, offered when she answered yes. */
export const PT_PLAN_CHOICE_KEY = "pt_plan_choice";
/**
 * The same choice with only the coach option, offered when she answered no
 * or unsure. The form renderer cannot hide one option of a field, so the
 * unclear case is its own field.
 */
export const PT_PLAN_COACH_ONLY_KEY = "pt_plan_choice_coach_only";

/**
 * The server's issue code when "utan coach" is sent without twelve months
 * behind it (FormsService). Shared so the app's translation of it (TES-1041)
 * cannot drift from what the server sends.
 */
export const PLAN_NOT_ELIGIBLE_ISSUE = "plan_not_eligible";

export const PLAN_CHOICE_WITH_COACH = "med_coach";
export const PLAN_CHOICE_WITHOUT_COACH = "utan_coach";

/** The option she picked, as the product she will be sent a link for. */
export const TRANSFER_PLAN_FOR_CHOICE: Readonly<
  Record<string, SubscriptionPlan>
> = {
  [PLAN_CHOICE_WITH_COACH]: "ella_uppstart",
  [PLAN_CHOICE_WITHOUT_COACH]: "ella_underhall",
};

// ---- labels (Emelie, verbatim) -----------------------------------------------

export const TRANSFER_PLAN_NAME: Record<SubscriptionPlan, string> = {
  ella_uppstart: "Med personlig coach",
  ella_underhall: "Utan personlig coach",
};

export const TRANSFER_PLAN_DESCRIPTION: Record<SubscriptionPlan, string> = {
  ella_uppstart:
    "personlig coachkontakt och veckovis stöd kring kost, träning, vanor, beteende och känslor, utöver den medicinska månadsuppföljningen.",
  ella_underhall:
    "medicinsk månadsuppföljning med läkare, men ingen personlig coachkontakt eller veckovis coachåterkoppling.",
};

/** "Med personlig coach – 995 kr/mån"; the price from the tenant's pricing. */
export function transferPlanOptionLabel(
  plan: SubscriptionPlan,
  amountMinor: number | null | undefined,
): string {
  const name = TRANSFER_PLAN_NAME[plan];
  if (amountMinor == null) return name;
  const kr = amountMinor / 100;
  const text = Number.isInteger(kr)
    ? String(kr)
    : kr.toFixed(2).replace(".", ",");
  return `${name} – ${text} kr/mån`;
}

// ---- eligibility -------------------------------------------------------------

export const TRANSFER_PLAN_UNCLEAR_REASONS = [
  // She does not know when the treatment started (or gave no date).
  "start_date_missing",
  // The start date is less than twelve months before she answered, although
  // she said twelve months in total: the answers contradict each other.
  "start_less_than_twelve_months",
  "answered_no",
  "answered_unsure",
  // An F-07 from before the question existed.
  "not_answered",
] as const;
export type TransferPlanUnclearReason =
  (typeof TRANSFER_PLAN_UNCLEAR_REASONS)[number];

export const transferPlanEligibilitySchema = z.object({
  status: z.enum(["eligible", "unclear"]),
  reasons: z.array(z.enum(TRANSFER_PLAN_UNCLEAR_REASONS)),
});
export type TransferPlanEligibility = z.infer<
  typeof transferPlanEligibilitySchema
>;

export const TRANSFER_PLAN_UNCLEAR_REASON_LABEL: Record<
  TransferPlanUnclearReason,
  string
> = {
  start_date_missing: "behandlingsstartdatum saknas",
  start_less_than_twelve_months:
    "startdatumet ligger mindre än tolv månader tillbaka",
  answered_no: "svarade nej på minst tolv månader",
  answered_unsure: "osäker på minst tolv månader",
  not_answered: "frågan ställdes inte i hennes version av formuläret",
};

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

// The same calendar day twelve months earlier, at UTC midnight. A 29 February
// falls back to 28 February in the earlier year.
function twelveMonthsBefore(at: Date): number {
  const y = at.getUTCFullYear() - 1;
  const m = at.getUTCMonth();
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return Date.UTC(y, m, Math.min(at.getUTCDate(), lastDay));
}

/**
 * Emelie point 5: eligible for both options when the start date lies at
 * least twelve months before she answered AND she answered yes to at least
 * twelve months in total. A pause is medically relevant but never makes her
 * ineligible by itself. Contradicting answers, no, unsure or a missing date
 * make eligibility unclear. `answeredAt` is the submission time, so the
 * result never changes after she has answered.
 */
export function transferPlanEligibility(
  answers: Readonly<Record<string, unknown>>,
  answeredAt: Date,
): TransferPlanEligibility {
  const reasons: TransferPlanUnclearReason[] = [];
  const total = answers[PT_TWELVE_MONTHS_KEY];
  if (total === undefined || total === null || total === "") {
    reasons.push("not_answered");
  } else if (total === "nej") {
    reasons.push("answered_no");
  } else if (total === "osaker") {
    reasons.push("answered_unsure");
  } else if (total !== "ja") {
    reasons.push("not_answered");
  }

  const startedAt = answers.pt_treatment_started_at;
  const match =
    answers.pt_treatment_start === "datum" && typeof startedAt === "string"
      ? ISO_DATE.exec(startedAt)
      : null;
  if (!match) {
    reasons.push("start_date_missing");
  } else {
    const start = Date.UTC(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
    );
    if (start > twelveMonthsBefore(answeredAt)) {
      reasons.push("start_less_than_twelve_months");
    }
  }
  return reasons.length === 0
    ? { status: "eligible", reasons: [] }
    : { status: "unclear", reasons };
}

/** The plan she chose in F-07, or null for an F-07 without the question. */
export function transferPlanChoice(
  answers: Readonly<Record<string, unknown>>,
): SubscriptionPlan | null {
  for (const key of [PT_PLAN_CHOICE_KEY, PT_PLAN_COACH_ONLY_KEY]) {
    const v = answers[key];
    if (typeof v === "string" && v in TRANSFER_PLAN_FOR_CHOICE) {
      return TRANSFER_PLAN_FOR_CHOICE[v]!;
    }
  }
  return null;
}

/**
 * Whether the choice she sent may stand: "utan coach" only when eligible.
 * The form offers it only after a yes; the server holds the date to it too.
 */
export function transferPlanChoiceAllowed(
  plan: SubscriptionPlan | null,
  eligibility: TransferPlanEligibility,
): boolean {
  return plan !== "ella_underhall" || eligibility.status === "eligible";
}

// ---- the doctor's exceptional correction (point 6) ---------------------------

// Emelie: he may correct it when she has changed her mind or there is a
// concrete reason; it needs a short reason and is logged. Ö-286 (a): the
// correction may also move a patient whose eligibility is unclear to the
// option without a coach, with the reason.
export const TRANSFER_PLAN_CORRECTION_REASON_MAX = 1000;
export const transferPlanCorrectionSchema = z.object({
  plan: subscriptionPlanSchema,
  reason: z.string().trim().min(1).max(TRANSFER_PLAN_CORRECTION_REASON_MAX),
});
export type TransferPlanCorrection = z.infer<
  typeof transferPlanCorrectionSchema
>;

/** What the transfer review shows about her choice. */
export const transferPlanChoiceViewSchema = z.object({
  // Her choice in F-07; null for an F-07 without the question.
  patientPlan: subscriptionPlanSchema.nullable(),
  eligibility: transferPlanEligibilitySchema.nullable(),
  correction: z
    .object({
      plan: subscriptionPlanSchema,
      reason: z.string(),
      correctedAt: z.string(),
      correctedBy: z.string().nullable(),
    })
    .nullable(),
  // What the subscription link after an approval is sent for: the
  // correction, else her choice, else the coach option.
  effectivePlan: subscriptionPlanSchema,
});
export type TransferPlanChoiceView = z.infer<
  typeof transferPlanChoiceViewSchema
>;

export function effectiveTransferPlan(
  patientPlan: SubscriptionPlan | null,
  correctedPlan: SubscriptionPlan | null,
): SubscriptionPlan {
  return correctedPlan ?? patientPlan ?? "ella_uppstart";
}
